// ============================================================================
// ROUTING FROM ACCESS RESOLVER
// Phase 2A: Single source of truth for routing based on access type
// ============================================================================

import type { AccessType } from "@/hooks/useAccessResolver";

/**
 * Determine the route path based on access type
 * This is the SINGLE SOURCE OF TRUTH for routing decisions
 */
export const routeFromAccess = (access: AccessType | undefined, isLoading: boolean): string | null => {
  if (isLoading || !access) {
    return null;
  }

  if (access.type === "single_task") {
    return `/clean/${access.propertyId}`;
  }

  if (access.type === "multiple_tasks" && access.taskIds.length > 0) {
    return "/cleaner/dashboard";
  }

  if (access.type === "full_dashboard") {
    if (access.workspaceLock === "jobs" || access.role === "cleaner") {
      return "/worker/schedule";
    }
    return "/owner/properties";
  }

  if (access.type === "invite_invalid") {
    return null;
  }

  if (access.type === "unauthorized") {
    return null;
  }

  if (access.type === "no_access") {
    return "/onboarding/decision";
  }

  if (process.env.NODE_ENV !== "production") {
    console.error("[routeFromAccess] Unhandled access type:", access);
  }
  return null;
};

/**
 * Check if a route is allowed for the given access type
 * Used to enforce single-task lockdown
 */
export const isRouteAllowed = (pathname: string, access: AccessType | undefined): boolean => {
  if (!access) {
    return false;
  }

  if (access.type === "single_task") {
    const taskPath = `/clean/${access.propertyId}`;
    const onTaskRoute = pathname === taskPath || pathname.startsWith(`${taskPath}/`);
    const onSchedule = pathname === "/worker/schedule" || pathname.startsWith("/worker/schedule");
    return onTaskRoute || onSchedule;
  }

  if (access.type === "multiple_tasks") {
    return (
      pathname.startsWith("/clean/") ||
      pathname.startsWith("/cleaner/") ||
      pathname === "/cleaner/dashboard"
    );
  }

  if (access.type === "full_dashboard") {
    return true;
  }

  if (access.type === "invite_invalid") {
    return false;
  }

  if (access.type === "unauthorized") {
    return false;
  }

  if (access.type === "no_access") {
    return false;
  }

  if (process.env.NODE_ENV !== "production") {
    console.error("[isRouteAllowed] Unhandled access type:", access);
  }
  return false;
};
